<template>
  <div class="component-testeditorconfig">
    <!-- Stage Configuration -->
    <v-card v-if="itemType === 'stage'" outlined>
      <v-card-title>
        <v-icon left>mdi-folder</v-icon>
        {{ $t('test.editor.config.stageConfig') }}
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="localItem.title"
          :label="$t('test.editor.config.fields.title')"
          outlined
          dense
          @input="onUpdate"
        ></v-text-field>

        <v-text-field
          v-model="localItem.subtitle"
          :label="$t('test.editor.config.fields.subtitle')"
          outlined
          dense
          @input="onUpdate"
        ></v-text-field>
      </v-card-text>
    </v-card>

    <!-- Question Configuration -->
    <v-card v-else-if="itemType === 'question'" outlined>
      <v-card-title>
        <v-icon left>mdi-help-circle</v-icon>
        {{ $t('test.editor.config.questionConfig') }}
      </v-card-title>

      <v-card-text>
        <v-textarea
          v-model="localItem.title"
          :label="$t('test.editor.config.fields.questionText')"
          outlined
          rows="2"
          auto-grow
          @input="onUpdate"
        ></v-textarea>

        <v-select
          v-model="localItem.type"
          :items="questionTypes"
          :label="$t('test.editor.config.fields.answerType')"
          outlined
          dense
          @input="onUpdate"
        ></v-select>
      </v-card-text>
    </v-card>

    <!-- Sezione Avanzate per Domanda -->
    <v-expansion-panels v-if="itemType === 'question'" v-model="showAdvancedSection" class="mt-4" flat>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div class="d-flex align-center">
            <v-icon left>mdi-cog</v-icon>
            <span>{{ $t('test.editor.config.advancedConfig') }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <h4 class="mb-2">{{ $t('test.editor.config.visibility.sectionTitle') }}</h4>
          <v-select
            v-model="localItem.visibility"
            :items="visibilityOptions"
            :label="$t('test.editor.config.visibility.behaviorLabel')"
            outlined
            dense
            @input="onUpdate"
          ></v-select>

          <v-alert
            v-if="localItem.visibility === 'always_visible'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.visibility.hideIfQuestion')"></span>
          </v-alert>

          <v-alert
            v-if="localItem.visibility === 'always_hidden'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.visibility.showIfQuestion')"></span>
          </v-alert>

          <div class="d-flex justify-space-between align-center mt-4 mb-2">
            <h4>{{ $t('test.editor.config.conditions.sectionTitle', { count: conditionCount }) }}</h4>
            <v-btn
              color="primary"
              small
              @click="addCondition">
              <v-icon left small>mdi-plus</v-icon>
              {{ $t('test.editor.config.conditions.newCondition') }}
            </v-btn>
          </div>

          <div v-if="localItem.conditions && localItem.conditions.length > 0">
            <template v-for="(condition, index) in localItem.conditions">
              <v-card
                :key="`condition-${index}`"
                outlined
                class="mb-2">
                <v-card-text class="py-2">
                  <v-chip
                    small
                    :color="localItem.visibility === 'always_visible' ? 'error' : 'success'"
                    text-color="white"
                    class="mb-2">
                    <v-icon left small>{{ localItem.visibility === 'always_visible' ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    {{ localItem.visibility === 'always_visible' ? $t('test.editor.config.conditions.hideIf') : $t('test.editor.config.conditions.showIf') }}
                  </v-chip>

                  <v-select
                    v-model="condition.questionId"
                    :items="getPreviousQuestions()"
                    item-value="id"
                    item-text="title"
                    :label="$t('test.editor.config.conditions.questionLabel')"
                    outlined
                    dense
                    @input="onConditionQuestionChange(condition)"
                  ></v-select>

                  <v-select
                    v-model="condition.answerIds"
                    :items="getAnswersForQuestion(condition.questionId)"
                    item-value="id"
                    item-text="title"
                    :label="$t('test.editor.config.conditions.selectedAnswers')"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    :disabled="!condition.questionId"
                    @input="onUpdate"
                  ></v-select>

                  <div class="d-flex justify-end">
                    <v-btn
                      color="error"
                      small
                      outlined
                      @click="removeCondition(index)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Chip "oppure" tra le condizioni (tranne l'ultima) -->
              <div v-if="index < localItem.conditions.length - 1" :key="`or-${index}`" class="d-flex justify-center my-2">
                <v-chip
                  small
                  color="orange"
                  text-color="white"
                  class="px-3">
                  {{ $t('test.editor.config.conditions.or') }}
                </v-chip>
              </div>
            </template>
          </div>

          <v-alert v-else type="info" text>
            {{ $t('test.editor.config.conditions.noConditions') }}
          </v-alert>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Phase Configuration -->
    <v-card v-else-if="itemType === 'phase'" outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left>mdi-cog-transfer</v-icon>
        {{ $t('test.editor.config.phaseConfig') }}
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="localItem.title"
          :label="$t('test.editor.config.fields.phaseName')"
          outlined
          dense
          hide-details
          @input="onUpdate"
        ></v-text-field>

        <v-text-field
          v-model="localItem.subtitle"
          :label="$t('test.editor.config.fields.subtitle')"
          :placeholder="$t('test.editor.config.fields.subtitleOptional')"
          outlined
          dense
          hide-details
          class="mt-4"
          @input="onUpdate"
        ></v-text-field>

        <!-- Campo di testo per la procedura -->
        <div class="mt-4 ckeditor-compact">
          <v-label class="v-label theme--light mb-2">{{ $t('test.editor.config.fields.procedureText') }}</v-label>
          <ckeditor
            :key="'phase-' + (localItem && localItem.id)"
            v-model="localItem.procedure"
            :editor="editor"
            :config="editorConfig"
            @input="onUpdate"
          ></ckeditor>
        </div>
      </v-card-text>
    </v-card>

    <!-- Sezione Avanzate per Fase -->
    <v-expansion-panels v-if="itemType === 'phase'" v-model="showAdvancedSection" class="mt-4" flat>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div class="d-flex align-center">
            <v-icon left>mdi-cog</v-icon>
            <span>{{ $t('test.editor.config.advancedConfig') }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <h4 class="mb-2">{{ $t('test.editor.config.visibility.sectionTitle') }}</h4>
          <v-select
            v-model="localItem.visibility"
            :items="visibilityOptions"
            :label="$t('test.editor.config.visibility.behaviorLabel')"
            outlined
            dense
            @input="onUpdate"
          ></v-select>

          <v-alert
            v-if="localItem.visibility === 'always_visible'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.visibility.hideIfPhase')"></span>
          </v-alert>

          <v-alert
            v-if="localItem.visibility === 'always_hidden'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.visibility.showIfPhase')"></span>
          </v-alert>

          <div class="d-flex justify-space-between align-center mt-4 mb-2">
            <h4>{{ $t('test.editor.config.conditions.sectionTitle', { count: conditionCount }) }}</h4>
            <v-btn
              color="primary"
              small
              @click="addCondition">
              <v-icon left small>mdi-plus</v-icon>
              {{ $t('test.editor.config.conditions.newCondition') }}
            </v-btn>
          </div>

          <div v-if="localItem.conditions && localItem.conditions.length > 0">
            <template v-for="(condition, index) in localItem.conditions">
              <v-card
                :key="`condition-${index}`"
                outlined
                class="mb-2">
                <v-card-text class="py-2">
                  <v-chip
                    small
                    :color="localItem.visibility === 'always_visible' ? 'error' : 'success'"
                    text-color="white"
                    class="mb-2">
                    <v-icon left small>{{ localItem.visibility === 'always_visible' ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    {{ localItem.visibility === 'always_visible' ? $t('test.editor.config.conditions.hideIf') : $t('test.editor.config.conditions.showIf') }}
                  </v-chip>

                  <v-select
                    v-model="condition.questionId"
                    :items="getPreviousQuestions()"
                    item-value="id"
                    item-text="title"
                    :label="$t('test.editor.config.conditions.questionLabel')"
                    outlined
                    dense
                    @input="onConditionQuestionChange(condition)"
                  ></v-select>

                  <v-select
                    v-model="condition.answerIds"
                    :items="getAnswersForQuestion(condition.questionId)"
                    item-value="id"
                    item-text="title"
                    :label="$t('test.editor.config.conditions.selectedAnswers')"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    :disabled="!condition.questionId"
                    @input="onUpdate"
                  ></v-select>

                  <div class="d-flex justify-end">
                    <v-btn
                      color="error"
                      small
                      outlined
                      @click="removeCondition(index)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Chip "oppure" tra le condizioni (tranne l'ultima) -->
              <div v-if="index < localItem.conditions.length - 1" :key="`or-${index}`" class="d-flex justify-center my-2">
                <v-chip
                  small
                  color="orange"
                  text-color="white"
                  class="px-3">
                  {{ $t('test.editor.config.conditions.or') }}
                </v-chip>
              </div>
            </template>
          </div>

          <v-alert v-else type="info" text>
            {{ $t('test.editor.config.conditions.noConditions') }}
          </v-alert>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Choice Configuration -->
    <v-card v-else-if="itemType === 'choice'" outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left>mdi-format-list-text</v-icon>
        {{ $t('test.editor.config.choiceConfig') }}
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="localItem.title"
          :label="$t('test.editor.config.fields.choiceName')"
          :placeholder="$t('test.editor.config.fields.choiceNamePlaceholder')"
          outlined
          dense
          hide-details
          @input="onUpdate"
        ></v-text-field>

        <!-- Campo di testo per la procedura -->
        <div class="mt-4 ckeditor-compact">
          <v-label class="v-label theme--light mb-2">{{ $t('test.editor.config.fields.procedureText') }}</v-label>
          <ckeditor
            :key="'choice-' + (localItem && localItem.id)"
            v-model="localItem.procedure"
            :editor="editor"
            :config="editorConfig"
            @input="onUpdate"
          ></ckeditor>
        </div>

        <!-- Prodotti associati -->
        <div class="mt-4">
          <v-label class="v-label theme--light mb-2">{{ $t('test.editor.config.fields.associatedProducts') }}</v-label>

          <!-- Selettore prodotti -->
          <v-autocomplete
            v-model="selectedProductId"
            :items="sortedAvailableProducts"
            item-value="id"
            :item-text="getProductDisplayName"
            :label="$t('test.editor.config.fields.addProduct')"
            outlined
            dense
            hide-details
            clearable
            class="mb-3"
            @change="addProduct"
          >
            <template v-slot:item="{ item }">
              <v-list-item-avatar tile size="32" class="mr-2">
                <v-img
                  v-if="hasProductImage(item)"
                  :src="item.imageUrl"
                  aspect-ratio="1"
                ></v-img>
                <v-img
                  v-else
                  src="../assets/product-placeholder.png"
                  aspect-ratio="1"
                ></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ getProductDisplayName(item) }}</v-list-item-title>
                <v-list-item-subtitle v-if="(item.description && (item.description.it || item.description))">
                  {{ (item.description && (item.description.it || item.description)) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <!-- Lista prodotti selezionati (riordinabile) -->
          <v-list v-if="selectedProducts.length > 0" dense class="pa-0">
            <draggable
              :list="selectedProducts"
              handle=".product-sort-handle"
              @end="onProductsReorder"
            >
              <ProductListItem
                v-for="product in selectedProducts"
                :key="product.id"
                :product-id="product.id"
              >
                <template #actions>
                  <v-btn icon small class="product-sort-handle" style="cursor: move;">
                    <v-icon small>mdi-drag</v-icon>
                  </v-btn>
                  <v-btn icon small @click="removeProduct(product.id)">
                    <v-icon small color="error">mdi-close</v-icon>
                  </v-btn>
                </template>
              </ProductListItem>
            </draggable>
          </v-list>

          <!-- Messaggio se nessun prodotto selezionato -->
          <div v-else class="text-center py-4">
            <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-package-variant</v-icon>
            <p class="text--secondary mb-0">{{ $t('test.editor.config.fields.noProducts') }}</p>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Sezione Avanzate per Scelta -->
    <v-expansion-panels v-if="itemType === 'choice'" v-model="showAdvancedSection" class="mt-4" flat>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div class="d-flex align-center">
            <v-icon left>mdi-cog</v-icon>
            <span>{{ $t('test.editor.config.advancedConfig') }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <!-- Sezione Consigliata -->
          <h4 class="mb-2">{{ $t('test.editor.config.recommended.sectionTitle') }}</h4>
          <v-select
            v-model="localItem.recommended"
            :items="recommendedOptions"
            :label="$t('test.editor.config.recommended.behaviorLabel')"
            outlined
            dense
            @input="onUpdate"
          ></v-select>

          <v-alert
            v-if="localItem.recommended === 'always'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.recommended.alwaysAlert')"></span>
          </v-alert>

          <v-alert
            v-if="localItem.recommended === 'conditions'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.recommended.conditionsAlert')"></span>
          </v-alert>

          <!-- Condizioni per consigliata -->
          <template v-if="localItem.recommended === 'conditions'">
            <div class="d-flex justify-space-between align-center mt-4 mb-2">
              <h4>{{ $t('test.editor.config.recommended.conditionsSectionTitle', { count: recommendedConditionCount }) }}</h4>
              <v-btn
                color="amber darken-2"
                small
                dark
                @click="addRecommendedCondition">
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('test.editor.config.conditions.newCondition') }}
              </v-btn>
            </div>

            <div v-if="localItem.recommendedConditions && localItem.recommendedConditions.length > 0">
              <template v-for="(condition, index) in localItem.recommendedConditions">
                <v-card
                  :key="`rec-condition-${index}`"
                  outlined
                  class="mb-2">
                  <v-card-text class="py-2">
                    <v-chip
                      small
                      color="amber darken-2"
                      text-color="white"
                      class="mb-2">
                      <v-icon left small>mdi-star</v-icon>
                      {{ $t('test.editor.config.recommended.recommendIf') }}
                    </v-chip>

                    <v-select
                      v-model="condition.questionId"
                      :items="getPreviousQuestions()"
                      item-value="id"
                      item-text="title"
                      :label="$t('test.editor.config.conditions.questionLabel')"
                      outlined
                      dense
                      @input="onUpdate"
                    ></v-select>

                    <v-select
                      v-model="condition.answerIds"
                      :items="getAnswersForQuestion(condition.questionId)"
                      item-value="id"
                      item-text="title"
                      :label="$t('test.editor.config.conditions.selectedAnswers')"
                      outlined
                      dense
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      :disabled="!condition.questionId"
                      @input="onUpdate"
                    ></v-select>

                    <div class="d-flex justify-end">
                      <v-btn
                        color="error"
                        small
                        outlined
                        @click="removeRecommendedCondition(index)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <div v-if="index < localItem.recommendedConditions.length - 1" :key="`rec-or-${index}`" class="d-flex justify-center my-2">
                  <v-chip
                    small
                    color="orange"
                    text-color="white"
                    class="px-3">
                    {{ $t('test.editor.config.conditions.or') }}
                  </v-chip>
                </div>
              </template>
            </div>

            <v-alert v-else type="info" text>
              {{ $t('test.editor.config.recommended.noConditions') }}
            </v-alert>
          </template>

          <!-- Sezione Visibilità -->
          <v-divider class="my-4"></v-divider>

          <h4 class="mb-2">{{ $t('test.editor.config.visibility.sectionTitle') }}</h4>
          <v-select
            v-model="localItem.visibility"
            :items="visibilityOptions"
            :label="$t('test.editor.config.visibility.behaviorLabel')"
            outlined
            dense
            @input="onUpdate"
          ></v-select>

          <v-alert
            v-if="localItem.visibility === 'always_visible'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.visibility.hideIfChoice')"></span>
          </v-alert>

          <v-alert
            v-if="localItem.visibility === 'always_hidden'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.visibility.showIfChoice')"></span>
          </v-alert>

          <div class="d-flex justify-space-between align-center mt-4 mb-2">
            <h4>{{ $t('test.editor.config.conditions.sectionTitle', { count: conditionCount }) }}</h4>
            <v-btn
              color="primary"
              small
              @click="addCondition">
              <v-icon left small>mdi-plus</v-icon>
              {{ $t('test.editor.config.conditions.newCondition') }}
            </v-btn>
          </div>

          <div v-if="localItem.conditions && localItem.conditions.length > 0">
            <template v-for="(condition, index) in localItem.conditions">
              <v-card
                :key="`condition-${index}`"
                outlined
                class="mb-2">
                <v-card-text class="py-2">
                  <v-chip
                    small
                    :color="localItem.visibility === 'always_visible' ? 'error' : 'success'"
                    text-color="white"
                    class="mb-2">
                    <v-icon left small>{{ localItem.visibility === 'always_visible' ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    {{ localItem.visibility === 'always_visible' ? $t('test.editor.config.conditions.hideIf') : $t('test.editor.config.conditions.showIf') }}
                  </v-chip>

                  <v-select
                    v-model="condition.questionId"
                    :items="getPreviousQuestions()"
                    item-value="id"
                    item-text="title"
                    :label="$t('test.editor.config.conditions.questionLabel')"
                    outlined
                    dense
                    @input="onConditionQuestionChange(condition)"
                  ></v-select>

                  <v-select
                    v-model="condition.answerIds"
                    :items="getAnswersForQuestion(condition.questionId)"
                    item-value="id"
                    item-text="title"
                    :label="$t('test.editor.config.conditions.selectedAnswers')"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    :disabled="!condition.questionId"
                    @input="onUpdate"
                  ></v-select>

                  <div class="d-flex justify-end">
                    <v-btn
                      color="error"
                      small
                      outlined
                      @click="removeCondition(index)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Chip "oppure" tra le condizioni (tranne l'ultima) -->
              <div v-if="index < localItem.conditions.length - 1" :key="`or-${index}`" class="d-flex justify-center my-2">
                <v-chip
                  small
                  color="orange"
                  text-color="white"
                  class="px-3">
                  {{ $t('test.editor.config.conditions.or') }}
                </v-chip>
              </div>
            </template>
          </div>

          <v-alert v-else type="info" text>
            {{ $t('test.editor.config.conditions.noConditions') }}
          </v-alert>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Answer Configuration -->
    <v-card v-else-if="itemType === 'answer'" outlined>
      <v-card-title>
        <v-icon left>mdi-format-list-bulleted</v-icon>
        {{ $t('test.editor.config.answerConfig') }}
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="localItem.title"
          :label="$t('test.editor.config.fields.answerText')"
          outlined
          dense
          @input="onUpdate"
        ></v-text-field>
      </v-card-text>
    </v-card>

    <!-- Sezione Avanzate per Risposta -->
    <v-expansion-panels v-if="itemType === 'answer'" v-model="showAdvancedSection" class="mt-4" flat>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div class="d-flex align-center">
            <v-icon left>mdi-cog</v-icon>
            <span>{{ $t('test.editor.config.advancedConfig') }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <h4 class="mb-2">{{ $t('test.editor.config.visibility.sectionTitle') }}</h4>
          <v-select
            v-model="localItem.visibility"
            :items="visibilityOptions"
            :label="$t('test.editor.config.visibility.behaviorLabel')"
            outlined
            dense
            @input="onUpdate"
          ></v-select>

          <v-alert
            v-if="localItem.visibility === 'always_visible'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.visibility.hideIfAnswer')"></span>
          </v-alert>

          <v-alert
            v-if="localItem.visibility === 'always_hidden'"
            type="info"
            text
            dense
            class="mt-2">
            <span v-html="$t('test.editor.config.visibility.showIfAnswer')"></span>
          </v-alert>

          <div class="d-flex justify-space-between align-center mt-4 mb-2">
            <h4>{{ $t('test.editor.config.conditions.sectionTitle', { count: conditionCount }) }}</h4>
            <v-btn
              color="primary"
              small
              @click="addCondition">
              <v-icon left small>mdi-plus</v-icon>
              {{ $t('test.editor.config.conditions.newCondition') }}
            </v-btn>
          </div>

          <div v-if="localItem.conditions && localItem.conditions.length > 0">
            <template v-for="(condition, index) in localItem.conditions">
              <v-card
                :key="`condition-${index}`"
                outlined
                class="mb-2">
                <v-card-text class="py-2">
                  <v-chip
                    small
                    :color="localItem.visibility === 'always_visible' ? 'error' : 'success'"
                    text-color="white"
                    class="mb-2">
                    <v-icon left small>{{ localItem.visibility === 'always_visible' ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    {{ localItem.visibility === 'always_visible' ? $t('test.editor.config.conditions.hideIf') : $t('test.editor.config.conditions.showIf') }}
                  </v-chip>

                  <v-select
                    v-model="condition.questionId"
                    :items="getPreviousQuestions()"
                    item-value="id"
                    item-text="title"
                    :label="$t('test.editor.config.conditions.questionLabel')"
                    outlined
                    dense
                    @input="onConditionQuestionChange(condition)"
                  ></v-select>

                  <v-select
                    v-model="condition.answerIds"
                    :items="getAnswersForQuestion(condition.questionId)"
                    item-value="id"
                    item-text="title"
                    :label="$t('test.editor.config.conditions.selectedAnswers')"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    :disabled="!condition.questionId"
                    @input="onUpdate"
                  ></v-select>

                  <div class="d-flex justify-end">
                    <v-btn
                      color="error"
                      small
                      outlined
                      @click="removeCondition(index)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Chip "oppure" tra le condizioni (tranne l'ultima) -->
              <div v-if="index < localItem.conditions.length - 1" :key="`or-${index}`" class="d-flex justify-center my-2">
                <v-chip
                  small
                  color="orange"
                  text-color="white"
                  class="px-3">
                  {{ $t('test.editor.config.conditions.or') }}
                </v-chip>
              </div>
            </template>
          </div>

          <v-alert v-else type="info" text>
            {{ $t('test.editor.config.conditions.noConditions') }}
          </v-alert>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
// import { v4 as uuidv4 } from 'uuid';
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials';
import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold';
import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import LinkPlugin from '@ckeditor/ckeditor5-link/src/link';
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Font from '@ckeditor/ckeditor5-font/src/font';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat';
import List from '@ckeditor/ckeditor5-list/src/list';
import draggable from 'vuedraggable';
import { mapActions } from 'vuex';
import ProductListItem from '@/components/ProductListItem';

export default {
  name: 'TestEditorConfig',

  components: {
    draggable,
    ProductListItem,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    itemType: {
      type: String,
      required: true,
    },
    test: {
      type: Array,
      required: true,
    },
    stageIndex: {
      type: Number,
      default: null,
    },
    questionIndex: {
      type: Number,
      default: null,
    },
  },

  data: () => ({
    localItem: null,
    showAdvancedSection: null,
    selectedProductId: null,

    editor: ClassicEditor,
    editorConfig: {
      placeholder: '',
      language: 'it',
      plugins: [
        EssentialsPlugin,
        BoldPlugin,
        ItalicPlugin,
        Underline,
        LinkPlugin,
        ParagraphPlugin,
        Alignment,
        Font,
        RemoveFormat,
        List,
      ],
      toolbar: {
        items: [
          'bold', 'italic', 'underline',
          '|',
          'alignment:left', 'alignment:right', 'alignment:center',
          '|',
          'link', 'numberedList', 'bulletedList',
          '|',
          'undo', 'redo', 'removeFormat',
        ],
        shouldNotGroupWhenFull: true,
      },
    },
  }),

  created() {
    // Inizializza i prodotti quando il componente viene creato
    this.initProducts();
  },

  watch: {
    item: {
      handler(newItem) {
        this.localItem = JSON.parse(JSON.stringify(newItem));
        // Migra condizioni dal vecchio formato (answerId) al nuovo (answerIds)
        this.migrateConditions(this.localItem.conditions);
        this.migrateConditions(this.localItem.recommendedConditions);
        // Apri automaticamente la sezione avanzate se necessario
        this.$nextTick(() => {
          this.showAdvancedSection = this.shouldShowAdvancedSection ? 0 : null;
        });
      },
      immediate: true,
      deep: true,
    },

    shouldShowAdvancedSection(newValue) {
      // Se la sezione dovrebbe essere mostrata, aprila automaticamente
      if (newValue) {
        this.$nextTick(() => {
          this.showAdvancedSection = 0;
        });
      }
    },
  },

  computed: {
    conditionCount() {
      return (this.localItem && this.localItem.conditions && this.localItem.conditions.length) || 0;
    },

    recommendedConditionCount() {
      return (this.localItem && this.localItem.recommendedConditions && this.localItem.recommendedConditions.length) || 0;
    },

    shouldShowAdvancedSection() {
      // Mostra la sezione avanzate se:
      // 1. La visibilità non è 'always_visible' OPPURE
      // 2. Ci sono condizioni configurate OPPURE
      // 3. La scelta è consigliata (recommended !== 'never')
      return this.localItem && (
        this.localItem.visibility !== 'always_visible'
        || this.conditionCount > 0
        || (this.localItem.recommended && this.localItem.recommended !== 'never')
        || this.recommendedConditionCount > 0
      );
    },

    products() {
      return this.$store.getters['products/docs'];
    },

    availableProducts() {
      if (!this.products || !this.localItem || !this.localItem.products) {
        return this.products || [];
      }

      // Filtra i prodotti già selezionati
      return this.products.filter((product) => !this.localItem.products.includes(product.id));
    },

    sortedAvailableProducts() {
      return [...this.availableProducts].sort((a, b) => {
        const nameA = this.getProductDisplayName(a).toLowerCase();
        const nameB = this.getProductDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    },

    selectedProducts() {
      if (!this.localItem || !this.localItem.products || !this.products) return [];
      return this.localItem.products
        .map((id) => this.products.find((p) => p.id === id))
        .filter(Boolean);
    },

    questionTypes() {
      return [
        { value: 'buttons', text: this.$t('test.editor.questionTypes.buttons') },
        { value: 'radio', text: this.$t('test.editor.questionTypes.radio') },
        { value: 'checkboxes', text: this.$t('test.editor.questionTypes.checkboxes') },
      ];
    },

    visibilityOptions() {
      return [
        { value: 'always_visible', text: this.$t('test.editor.config.visibility.alwaysVisible') },
        { value: 'always_hidden', text: this.$t('test.editor.config.visibility.alwaysHidden') },
      ];
    },

    recommendedOptions() {
      return [
        { value: 'never', text: this.$t('test.editor.config.recommended.never') },
        { value: 'always', text: this.$t('test.editor.config.recommended.always') },
        { value: 'conditions', text: this.$t('test.editor.config.recommended.withConditions') },
      ];
    },
  },

  methods: {
    ...mapActions('products', {
      initProducts: 'INIT_PRODUCTS',
    }),

    onUpdate() {
      this.$emit('update', this.localItem);
    },

    deleteItem() {
      this.$emit('delete');
    },

    // Stage methods

    // Question methods

    // Condition methods
    addCondition() {
      if (!this.localItem.conditions) {
        this.$set(this.localItem, 'conditions', []);
      }

      this.localItem.conditions.push({
        questionId: null,
        answerIds: [],
      });

      this.onUpdate();
    },

    removeCondition(index) {
      // eslint-disable-next-line no-alert
      if (confirm(this.$t('test.editor.config.conditions.removeConfirm'))) {
        this.localItem.conditions.splice(index, 1);
        this.onUpdate();
      }
    },

    addRecommendedCondition() {
      if (!this.localItem.recommendedConditions) {
        this.$set(this.localItem, 'recommendedConditions', []);
      }
      this.localItem.recommendedConditions.push({ questionId: null, answerIds: [] });
      this.onUpdate();
    },

    removeRecommendedCondition(index) {
      // eslint-disable-next-line no-alert
      if (confirm(this.$t('test.editor.config.conditions.removeConfirm'))) {
        this.localItem.recommendedConditions.splice(index, 1);
        this.onUpdate();
      }
    },

    onConditionQuestionChange(condition) {
      // Reset delle risposte selezionate quando si cambia domanda
      this.$set(condition, 'answerIds', []);
      this.onUpdate();
    },

    // Migra condizioni dal vecchio formato { questionId, answerId } al nuovo { questionId, answerIds }
    migrateConditions(conditions) {
      if (!conditions || !Array.isArray(conditions)) return;
      conditions.forEach((c) => {
        if (!Array.isArray(c.answerIds) || c.answerIds.length === 0) {
          // answerIds assente, vuoto o non-array: usa answerId come fallback
          this.$set(c, 'answerIds', c.answerId ? [c.answerId] : []);
        }
        this.$delete(c, 'answerId');
      });
    },

    // Helper methods
    getPreviousQuestions() {
      const questions = [];

      // Trova l'indice dello stage corrente
      const currentStageIndex = this.getCurrentStageIndex();

      // Aggiungi domande dagli stage precedenti
      for (let stageIndex = 0; stageIndex < currentStageIndex; stageIndex++) {
        const stage = this.test[stageIndex];
        if (stage.questions) {
          stage.questions.forEach((question) => {
            questions.push({
              id: question.id,
              title: `${question.title}`,
            });
          });
        }
      }

      // Aggiungi domande precedenti dello stesso stage (se stiamo configurando una domanda)
      if (this.itemType === 'question' || this.itemType === 'answer') {
        const currentStage = this.test[currentStageIndex];
        if (currentStage && currentStage.questions) {
          const currentQuestionIndex = this.getCurrentQuestionIndex();
          for (let questionIndex = 0; questionIndex < currentQuestionIndex; questionIndex++) {
            const question = currentStage.questions[questionIndex];
            questions.push({
              id: question.id,
              title: `${question.title}`,
            });
          }
        }
      }

      return questions;
    },

    getAnswersForQuestion(questionId) {
      if (!questionId) return [];

      // Trova la domanda nell'intero test
      const stage = this.test.find((s) => s.questions && s.questions.some((q) => q.id === questionId));
      if (stage) {
        const question = stage.questions.find((q) => q.id === questionId);
        if (question && question.answers && question.answers.length > 0) {
          return question.answers.map((answer) => ({
            id: answer.id,
            title: answer.title,
          }));
        }
      }

      return [];
    },

    getCurrentStageIndex() {
      return this.stageIndex !== null ? this.stageIndex : 0;
    },

    getCurrentQuestionIndex() {
      return this.questionIndex !== null ? this.questionIndex : 0;
    },

    // Metodi per gestire i prodotti
    addProduct() {
      if (!this.selectedProductId || !this.localItem) {
        return;
      }

      // Inizializza l'array products se non esiste
      if (!this.localItem.products) {
        this.$set(this.localItem, 'products', []);
      }

      // Aggiungi il prodotto se non è già presente
      if (!this.localItem.products.includes(this.selectedProductId)) {
        this.localItem.products.push(this.selectedProductId);
        this.onUpdate();
      }

      // Reset del selettore
      this.selectedProductId = null;
    },

    removeProduct(productId) {
      if (!this.localItem || !this.localItem.products) {
        return;
      }

      const index = this.localItem.products.indexOf(productId);
      if (index > -1) {
        this.localItem.products.splice(index, 1);
        this.onUpdate();
      }
    },

    getProductName(productId) {
      const product = this.products.find((p) => p.id === productId);
      return product
        ? ((product.name && (product.name.it || product.name)) || `Prodotto ${productId}`)
        : `Prodotto ${productId}`;
    },

    getProductDisplayName(product) {
      return (product.name && (product.name.it || product.name)) || `Prodotto ${product.id}`;
    },

    hasProductImage(product) {
      return product && product.imageUrl !== null;
    },

    onProductsReorder() {
      this.localItem.products = this.selectedProducts.map((p) => p.id);
      this.onUpdate();
    },
  },
};
</script>

<style scoped>
.component-testeditorconfig {
  height: 100%;
}

.v-list-item {
  border-radius: 4px;
  margin-bottom: 4px;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

</style>
